import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  getBirthdaysToday,
  getWorkAnniversariesToday,
} from "../../../app/utils/common/dashboardEvents";
import { DEFAULT_PROFILE_IMAGE } from "../../../app/utils/common/baseUrl";
import { sendBirthdayEmail } from "../../../app/utils/common/birthdayEmail";
import { sendWorkAnniversaryEmail } from "../../../app/utils/common/workAnniversaryEmail";
import { showErrorResponse } from "../../../app/utils/common/Functions";
import { showSuccessToast } from "../../../app/utils/common/Toast";
import "./DashboardCelebrations.scss";

const DISMISS_KEY = "tcs-dashboard-celebrations-dismiss";

const DashboardCelebrations = ({
  employees = [],
  canSendCelebrationEmail = false,
  sentBirthdayEmailUserIds = [],
  onBirthdayEmailSent,
  sentAnniversaryEmailUserIds = [],
  onAnniversaryEmailSent,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [sendingBirthdayUserId, setSendingBirthdayUserId] = useState(null);
  const [sendingAnniversaryUserId, setSendingAnniversaryUserId] = useState(null);

  const birthdays = useMemo(() => getBirthdaysToday(employees), [employees]);
  const anniversaries = useMemo(() => getWorkAnniversariesToday(employees), [employees]);

  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === today);
    } catch (_e) {
      setDismissed(false);
    }
  }, [employees, birthdays.length, anniversaries.length]);

  const sentBirthdayIdSet = useMemo(
    () => new Set(sentBirthdayEmailUserIds.map((id) => Number(id))),
    [sentBirthdayEmailUserIds]
  );

  const sentAnniversaryIdSet = useMemo(
    () => new Set(sentAnniversaryEmailUserIds.map((id) => Number(id))),
    [sentAnniversaryEmailUserIds]
  );

  const handleSendBirthdayEmail = useCallback(
    async (userId) => {
      if (!userId || sendingBirthdayUserId || sentBirthdayIdSet.has(Number(userId))) return;
      setSendingBirthdayUserId(userId);
      try {
        const res = await sendBirthdayEmail(userId);
        showSuccessToast(res?.message || "Birthday email sent.");
        if (typeof onBirthdayEmailSent === "function") {
          onBirthdayEmailSent(userId);
        }
      } catch (err) {
        showErrorResponse(err);
        const msg =
          err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          "";
        if (/already been sent/i.test(String(msg)) && typeof onBirthdayEmailSent === "function") {
          onBirthdayEmailSent(userId);
        }
      } finally {
        setSendingBirthdayUserId(null);
      }
    },
    [sendingBirthdayUserId, sentBirthdayIdSet, onBirthdayEmailSent]
  );

  const handleSendAnniversaryEmail = useCallback(
    async (userId) => {
      if (!userId || sendingAnniversaryUserId || sentAnniversaryIdSet.has(Number(userId))) return;
      setSendingAnniversaryUserId(userId);
      try {
        const res = await sendWorkAnniversaryEmail(userId);
        showSuccessToast(res?.message || "Work anniversary email sent.");
        if (typeof onAnniversaryEmailSent === "function") {
          onAnniversaryEmailSent(userId);
        }
      } catch (err) {
        showErrorResponse(err);
        const msg =
          err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          "";
        if (/already been sent/i.test(String(msg)) && typeof onAnniversaryEmailSent === "function") {
          onAnniversaryEmailSent(userId);
        }
      } finally {
        setSendingAnniversaryUserId(null);
      }
    },
    [sendingAnniversaryUserId, sentAnniversaryIdSet, onAnniversaryEmailSent]
  );

  const hasAny = birthdays.length > 0 || anniversaries.length > 0;
  if (!hasAny || dismissed) {
    return null;
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, moment().format("YYYY-MM-DD"));
    } catch (_e) {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div className="dashboard-celebrations" role="region" aria-label="Team celebrations">
      <div className="dashboard-celebrations__confetti" aria-hidden />

      {birthdays.length > 0 ? (
        <div className="dashboard-celebrations__card dashboard-celebrations__card--birthday">
          <div className="dashboard-celebrations__badge dashboard-celebrations__badge--birthday">
            <span aria-hidden>🎂</span> Birthday today
          </div>
          <h2 className="dashboard-celebrations__title">
            Happy Birthday
            <span className="dashboard-celebrations__sparkles" aria-hidden>
              ✨
            </span>
          </h2>
          <p className="dashboard-celebrations__message">
            Wishing a fantastic day filled with joy, laughter, and well-deserved celebration. The
            whole team is cheering for you!
          </p>
          <div className="dashboard-celebrations__people">
            {birthdays.map((p) => (
              <div
                key={`bd-celebrate-${p.userId}`}
                className="dashboard-celebrations__person dashboard-celebrations__person--with-action"
              >
                <img
                  src={p.profileImage || DEFAULT_PROFILE_IMAGE}
                  alt=""
                  onError={(e) => {
                    e.target.src = DEFAULT_PROFILE_IMAGE;
                  }}
                />
                <span>{p.name}</span>
                {canSendCelebrationEmail && !sentBirthdayIdSet.has(Number(p.userId)) ? (
                  <button
                    type="button"
                    className="dashboard-celebrations__send-email dashboard-celebrations__send-email--birthday"
                    disabled={sendingBirthdayUserId === p.userId}
                    onClick={() => handleSendBirthdayEmail(p.userId)}
                  >
                    {sendingBirthdayUserId === p.userId ? "Sending…" : "Send email"}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {anniversaries.length > 0 ? (
        <div className="dashboard-celebrations__card dashboard-celebrations__card--anniversary">
          <div className="dashboard-celebrations__badge">
            <span aria-hidden>🏆</span> Work anniversary
          </div>
          <h2 className="dashboard-celebrations__title">
            Happy Work Anniversary
            <span className="dashboard-celebrations__sparkles" aria-hidden>
              ✨
            </span>
          </h2>
          <p className="dashboard-celebrations__message">
            Thank you for your dedication, impact, and the energy you bring every day. Here&apos;s
            to continued success together — we&apos;re grateful you&apos;re on the team.
          </p>
          <div className="dashboard-celebrations__people">
            {anniversaries.map((p) => (
              <div
                key={`an-celebrate-${p.userId}`}
                className="dashboard-celebrations__person dashboard-celebrations__person--with-action"
              >
                <img
                  src={p.profileImage || DEFAULT_PROFILE_IMAGE}
                  alt=""
                  onError={(e) => {
                    e.target.src = DEFAULT_PROFILE_IMAGE;
                  }}
                />
                <span>
                  {p.name}
                  {p.years >= 1 ? (
                    <span className="text-xs text-[#5b21b6] font-semibold ml-1">
                      · {p.years} yr{p.years !== 1 ? "s" : ""}
                    </span>
                  ) : (
                    <span className="text-xs text-[#5b21b6] font-semibold ml-1">· celebrating you</span>
                  )}
                </span>
                {canSendCelebrationEmail && !sentAnniversaryIdSet.has(Number(p.userId)) ? (
                  <button
                    type="button"
                    className="dashboard-celebrations__send-email"
                    disabled={sendingAnniversaryUserId === p.userId}
                    onClick={() => handleSendAnniversaryEmail(p.userId)}
                  >
                    {sendingAnniversaryUserId === p.userId ? "Sending…" : "Send email"}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="dashboard-celebrations__toolbar">
        <button type="button" className="dashboard-celebrations__dismiss" onClick={dismiss}>
          Dismiss for today
        </button>
      </div>
    </div>
  );
};

export default DashboardCelebrations;
